import { instacarAppLanguageSchema } from "@instacover/helpers"
import { z } from "zod"

import { LOCAL_STORAGE_KEYS } from "../constants/localStorage"
import { safeLocalStorage } from "../utils/safeBrowserStorage"
import { safeJsonParse } from "../utils/std/string"
import type { InstacarAppLanguage } from "../utils/types"

// customerId is optional: an entry without one was picked on a screen that has no session, and
// counts as a global preference. Session callers check it against config.languages anyway.
const persistedLanguageSchema = z.object({
  customerId: z.string().optional(),
  language: instacarAppLanguageSchema,
})

/**
 * Language the user picked, or undefined when there is no usable entry. Passing a customerId
 * ignores an entry stored for a different customer; omitting it accepts whatever is stored.
 */
export function readPersistedLanguage(customerId?: string): InstacarAppLanguage | undefined {
  const stored = safeJsonParse(safeLocalStorage.getItem(LOCAL_STORAGE_KEYS.appLanguage))
  const parsed = persistedLanguageSchema.safeParse(stored)

  if (!parsed.success) {
    return undefined
  }

  const isForAnotherCustomer =
    customerId !== undefined && parsed.data.customerId !== undefined && parsed.data.customerId !== customerId

  if (isForAnotherCustomer) {
    return undefined
  }

  return parsed.data.language
}

/** Without a customerId the pick is stored as a global preference, for screens that have no session. */
export function persistLanguage(language: InstacarAppLanguage, customerId?: string) {
  safeLocalStorage.setItem(LOCAL_STORAGE_KEYS.appLanguage, JSON.stringify({ customerId, language }))
}
